/**
 * /auth/recovery/status -- Public recovery status tracker.
 *
 * Lets an unauthenticated user check on the status of a recovery request
 * they previously submitted via /auth/recovery/lost-both, using the
 * tracking reference emailed to them.
 *
 * Backend endpoint (`GET /v1/auth/recovery/status?token=...`) is not yet
 * live -- the admin-side review queue exists, but the anonymous public
 * lookup is gated on the email-delivery + token-signing work that lands
 * with the recovery flow rollout. Until then this page:
 *
 *   1. Accepts the tracking reference (from URL `?token=` or manual input)
 *   2. Shows an honest "lookup not yet available" message
 *   3. Directs the user to support@elreign.io with their reference
 *
 * NO fixture personas. NO fake records. Before 2026-05-18 this page
 * shipped a `FAKE_RECORDS` map with three named users (Anna van der Merwe
 * / Pravesh Naidoo / Lerato Mokoena) which leaked to every unauthenticated
 * visitor who reached the URL. Cleanup tracked under the 2026-05-18 P0
 * fixture-leak re-audit.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AuthLayout } from '../../../components/layouts/auth-layout';
import { BackToSignIn } from '../../../components/auth/back-to-sign-in';

export const Route = createFileRoute('/auth/recovery/status')({
  component: RecoveryStatusPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

function RecoveryStatusPage() {
  const search = Route.useSearch();
  const [refInput, setRefInput] = useState<string>(search.token ?? '');
  const [submitted, setSubmitted] = useState<string | null>(
    search.token ? search.token.trim() : null,
  );

  function handleLookup() {
    const trimmed = refInput.trim();
    if (trimmed.length === 0) return;
    setSubmitted(trimmed);
  }

  return (
    <AuthLayout
      eyebrow="Recovery status"
      heading="Track your request."
      sub="Enter the tracking reference from your recovery email to check on a previously-submitted request."
    >
      {/* Reference input */}
      <div className="field" style={{ marginBottom: 16 }}>
        <label className="field-label" htmlFor="recovery-ref-input">
          Tracking reference
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            id="recovery-ref-input"
            type="text"
            className="field-input"
            placeholder="e.g. ELR-T3-XXXXX"
            value={refInput}
            onChange={(e) => setRefInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            style={{
              flex: 1,
              fontFamily: 'var(--mono)',
              letterSpacing: '0.04em',
            }}
            aria-describedby="recovery-ref-hint"
          />
          <button
            type="button"
            onClick={handleLookup}
            style={{
              padding: '0 18px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--accent-400)',
              color: 'var(--primary-900)',
              fontFamily: 'var(--sans)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Look up
          </button>
        </div>
        <p
          id="recovery-ref-hint"
          style={{
            marginTop: 8,
            fontSize: 12.5,
            color: 'var(--ink-3)',
            lineHeight: 1.5,
          }}
        >
          Your reference was emailed to you after you submitted the
          recovery request. It looks like <span className="mono">ELR-T2-</span>
          or <span className="mono">ELR-T3-</span> followed by 5 digits.
        </p>
      </div>

      {/* Honest stub -- public status lookup not yet wired. */}
      {submitted && (
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: '16px 18px',
            borderRadius: 10,
            background: 'var(--accent-100)',
            border: '1px solid var(--accent-300)',
            color: 'var(--ink)',
            fontSize: 13.5,
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: 6,
              color: 'var(--accent-700)',
            }}
          >
            Reference{' '}
            <span className="mono" style={{ fontWeight: 500 }}>
              {submitted}
            </span>
          </div>
          <p style={{ margin: 0, color: 'var(--ink-2)' }}>
            Online status lookup is not yet available. Your request is in the
            admin review queue and a recovery officer will respond by email
            within the timeframe given when you submitted (24-72 hours
            for Tier&nbsp;2, up to 10 business days for Tier&nbsp;3).
          </p>
          <p style={{ margin: '10px 0 0', color: 'var(--ink-2)' }}>
            Need an update sooner? Email{' '}
            <a
              href={`mailto:support@elreign.io?subject=${encodeURIComponent(
                `Recovery status for ${submitted}`,
              )}`}
              style={{ color: 'var(--primary-700)', fontWeight: 500 }}
            >
              support@elreign.io
            </a>{' '}
            with your reference and we&rsquo;ll respond the same day.
          </p>
        </div>
      )}

      <p
        style={{
          marginTop: 20,
          fontSize: 12,
          color: 'var(--ink-4)',
          lineHeight: 1.55,
          textAlign: 'center',
        }}
      >
        Recovery decisions are final and logged for audit purposes. Elreign
        support: support@elreign.io
      </p>

      {/* DESIGN GATE B2: this lookup had no link at all apart from the logo. */}
      <BackToSignIn />
    </AuthLayout>
  );
}
